import { useEffect, useState } from 'react';
import { breakpoints } from '../../design/tokens.js';

/**
 * Subscribes to a CSS media query so React can branch on the same
 * breakpoints the stylesheets use (see `breakpoints` in design/tokens.ts).
 *
 * The initial state reads matchMedia synchronously so the first render
 * already matches the viewport; the listener keeps it live across resizes
 * and device rotation.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches,
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    // Re-read in case the query resolved differently after mount.
    setMatches(mql.matches);
    const onChange = (event: MediaQueryListEvent): void => setMatches(event.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** True while the viewport is at or below the mobile breakpoint. */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${breakpoints.mobileMax}px)`);
}
